import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, TextField,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getApartments, getMonthlyDashboard, getDateRangeDashboard, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockDashboard } from '../services/mockData';
import { useAuth } from '../context/AuthContext';
import SuperAdminDashboard from './SuperAdminDashboard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function ApartmentDashboard() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [filterMode, setFilterMode] = useState('monthly'); // 'monthly' or 'custom'
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      // Admin/Normal users only see their own apartment
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    setLoading(true);
    if (USE_MOCK_DATA) {
      setTimeout(() => { setDashboard(mockDashboard); setLoading(false); }, 300);
      return;
    }

    if (filterMode === 'custom' && fromDate && toDate) {
      getDateRangeDashboard(selectedApartment, fromDate, toDate)
        .then(res => setDashboard(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      getMonthlyDashboard(selectedApartment, month, year)
        .then(res => setDashboard(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [selectedApartment, month, year, filterMode, fromDate, toDate]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>My Apartment Dashboard</Typography>

      {/* Filter Mode Toggle */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={filterMode}
          exclusive
          onChange={(_, v) => { if (v) setFilterMode(v); }}
          size="small"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="custom">Custom Date Range</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {apartments.length > 1 && (
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Apartment</InputLabel>
              <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
                {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}

        {filterMode === 'monthly' ? (
          <>
            <Grid item xs={6} sm={apartments.length > 1 ? 3 : 4}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={apartments.length > 1 ? 3 : 4}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6} sm={apartments.length > 1 ? 3 : 4}>
              <TextField fullWidth size="small" label="From Date" type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={apartments.length > 1 ? 3 : 4}>
              <TextField fullWidth size="small" label="To Date" type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Grid>
          </>
        )}
      </Grid>

      {apartments.length === 1 && (
        <Typography variant="subtitle1" color="primary" sx={{ mb: 2 }}>
          {apartments[0].name}
        </Typography>
      )}

      {loading ? <CircularProgress /> : dashboard && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#fff5f5' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Expenses (This Month)</Typography>
                  <Typography variant="h4" color="error">₹{dashboard.totalExpenses.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#f0fff4' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Contributions (This Month)</Typography>
                  <Typography variant="h4" color="success.main">₹{dashboard.totalContributions.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: dashboard.balance >= 0 ? '#f0fff4' : '#fff5f5' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Monthly Balance</Typography>
                  <Typography variant="h4" color={dashboard.balance >= 0 ? 'success.main' : 'error'}>
                    ₹{dashboard.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Overall Financial Summary */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {dashboard.openingBalance > 0 && (
              <Grid item xs={12} sm={3}>
                <Card sx={{ bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>Opening Balance</Typography>
                    <Typography variant="h5" color="primary">₹{dashboard.openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} sm={dashboard.openingBalance > 0 ? 3 : 4}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Total Collected (All Time)</Typography>
                  <Typography variant="h5" color="success.main">₹{(dashboard.cumulativeContributions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={dashboard.openingBalance > 0 ? 3 : 4}>
              <Card sx={{ bgcolor: '#fce4ec' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Total Spent (All Time)</Typography>
                  <Typography variant="h5" color="error">₹{(dashboard.cumulativeExpenses || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={dashboard.openingBalance > 0 ? 3 : 4}>
              <Card sx={{ bgcolor: (dashboard.cumulativeBalance || 0) >= 0 ? '#e8f5e9' : '#fce4ec', border: '2px solid', borderColor: (dashboard.cumulativeBalance || 0) >= 0 ? 'success.main' : 'error.main' }}>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>Overall Balance (Fund Available)</Typography>
                  <Typography variant="h5" fontWeight="bold" color={(dashboard.cumulativeBalance || 0) >= 0 ? 'success.main' : 'error'}>
                    ₹{(dashboard.cumulativeBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Expenses by Category</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={dashboard.expensesByCategory} dataKey="amount" nameKey="categoryName" cx="50%" cy="50%" outerRadius={100} label>
                        {dashboard.expensesByCategory.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Expenses by Month</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboard.expensesByMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                      <Legend />
                      <Bar dataKey="amount" fill="#f44336" name="Expenses (₹)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// Main Dashboard component - routes to correct dashboard based on role
export default function Dashboard() {
  const { user } = useAuth();

  if (user?.roleName === 'SuperAdmin') {
    return <SuperAdminDashboard />;
  }

  return <ApartmentDashboard />;
}
